import { supabase } from './supabase';
import { logger } from '../utils/logger';

// Type definition for deletion request
interface DeletionRequest {
  id: string;
  user_id: string;
  email: string;
  requested_at: string;
  scheduled_deletion_at: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  verification_token?: string;
  verified_at?: string;
  completed_at?: string;
}

/**
 * Request account deletion with security safeguards
 * Implements:
 * - Password confirmation
 * - Email verification (via Edge Function)
 * - 30-day grace period
 * - Rate limiting (1 request per 24 hours)
 *
 * @param password - User's password for confirmation
 */
export async function deleteUserAccount(password: string): Promise<{
  success: boolean;
  error?: string;
  request?: any;
  rateLimited?: boolean;
}> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    if (!password) {
      return { success: false, error: 'Password confirmation required' };
    }

    logger.info('Requesting account deletion');

    // Call the delete-account Edge Function with proper security checks
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'request',
        password: password,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error from delete-account Edge Function:', result);
      return {
        success: false,
        error: result.error || 'Failed to request account deletion',
        rateLimited: result.rateLimited,
      };
    }

    logger.info('Account deletion request created successfully');

    return {
      success: true,
      request: result.request,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Exception in deleteUserAccount:', error);
    return { success: false, error: errorMessage || 'Failed to request account deletion' };
  }
}

/**
 * Alias for deleteUserAccount to maintain backwards compatibility with UI
 */
export const requestAccountDeletion = deleteUserAccount;

/**
 * Verify account deletion with verification token
 * @param verificationToken - Token sent via email
 */
export async function verifyAccountDeletion(verificationToken: string): Promise<{
  success: boolean;
  error?: string;
  request?: any;
}> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'verify',
        verificationToken: verificationToken,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error verifying deletion:', result.error);
      return { success: false, error: result.error || 'Failed to verify deletion' };
    }

    logger.info('Account deletion verified');

    return { success: true, request: result.request };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Exception in verifyAccountDeletion:', error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Cancel a pending account deletion request
 */
export async function cancelAccountDeletion(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'cancel',
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      logger.error('Error cancelling deletion:', result.error);
      return { success: false, error: result.error || 'Failed to cancel deletion' };
    }

    logger.info('Account deletion cancelled');

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Exception in cancelAccountDeletion:', error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Check if user has a pending deletion request
 *
 * @returns Deletion request details if found
 */
export async function getPendingDeletionRequest(): Promise<{
  success: boolean;
  error?: string;
  request?: DeletionRequest;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Query the account_deletion_requests table
    const { data, error } = await supabase
      .from('account_deletion_requests')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['pending', 'verified'])
      .order('requested_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching deletion request:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      request: data || undefined,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Exception in getPendingDeletionRequest:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * LEGACY: Immediate account deletion (kept for emergency use)
 * Use deleteUserAccount() instead for production
 *
 * @deprecated Use deleteUserAccount() instead
 */
export async function deleteUserAccountImmediate(): Promise<{
  success: boolean;
  error?: string;
  data?: any;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user found for account deletion');
      return { success: false, error: 'Not authenticated' };
    }

    logger.info('Starting immediate account deletion for user:', user.id);
    logger.warn('WARNING: Using legacy immediate deletion - should migrate to secure deletion flow');

    // Try calling the RPC function
    logger.log('Calling delete_user_account RPC...');
    const { data, error } = await supabase.rpc('delete_user_account', {
      target_user_id: user.id
    });

    if (error) {
      logger.error('Error from RPC delete_user_account:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete account',
      };
    }

    logger.info('Account deleted successfully via RPC');

    return {
      success: true,
      data,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Exception in deleteUserAccountImmediate:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
}
